import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const Cookies = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Cookiebeleid | Privacy & Cookies Transparantie | Compleet Verzekerd</title>
        <meta name="description" content="Cookiebeleid Compleet Verzekerd. Transparant over functionele cookies, externe diensten en uw privacy rechten. Beheer uw cookie-voorkeuren eenvoudig." />  
        <meta name="keywords" content="cookiebeleid, privacy cookies, functionele cookies, cookie beheer, website privacy, gdpr compliance, compleet verzekerd" />
        <link rel="canonical" href="https://compleetverzekerd.nl/cookies/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Cookiebeleid",
            "description": "Cookiebeleid en privacy informatie van Compleet Verzekerd",
            "publisher": {
              "@type": "Organization",
              "name": "Compleet Verzekerd"
            }
          })}
        </script>
      </Helmet>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded">
        Spring naar hoofdinhoud
      </a>
      <Header />
      
      <main id="main-content" className="pt-32 pb-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-primary mb-4">Cookiebeleid</h1>
            <p className="text-xl text-muted-foreground">
              Transparantie over het gebruik van cookies op onze website
            </p>
          </div>

          <div className="space-y-8">
            {/* Wat zijn cookies */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Wat zijn cookies?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Cookies zijn kleine tekstbestanden die op uw apparaat worden opgeslagen wanneer u een website bezoekt. 
                  Ze helpen websites om informatie over uw bezoek te onthouden, zoals uw voorkeuren en instellingen.
                </p>
                <p className="text-muted-foreground">
                  Op compleetverzekerd.nl gebruiken we alleen cookies die noodzakelijk zijn voor de werking van onze website 
                  en om u de beste gebruikerservaring te bieden. We gebruiken geen cookies voor reclamedoeleinden.
                </p>
              </CardContent>
            </Card>

            {/* Welke cookies gebruiken we */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Welke cookies gebruiken we?</CardTitle>
                <CardDescription>
                  Hieronder vindt u een overzicht van alle cookies die we gebruiken
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Functionele cookies */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-primary">1. Functionele cookies (altijd actief)</h3>
                    <p className="text-muted-foreground mb-4">
                      Deze cookies zijn essentieel voor de werking van onze website en kunnen niet worden uitgeschakeld.
                    </p>
                    
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div>
                        <h4 className="font-medium">Sessiebeheer</h4>
                        <p className="text-sm text-muted-foreground">
                          Bewaren tijdelijke informatie tijdens uw bezoek, zoals formuliergegevens
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-medium">Navigatie en voorkeuren</h4>
                        <p className="text-sm text-muted-foreground">
                          Onthouden uw taalvoorkeur en navigatiekeuzes
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-medium">Beveiliging</h4>
                        <p className="text-sm text-muted-foreground">
                          Beschermen tegen misbruik en zorgen voor veilige formulierverzending
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Third-party cookies */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-primary">2. Cookies van derden</h3>
                    <p className="text-muted-foreground mb-4">
                      We gebruiken enkele externe diensten die hun eigen cookies kunnen plaatsen:
                    </p>
                    
                    <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                      <div>
                        <h4 className="font-medium">Google Reviews</h4>
                        <p className="text-sm text-muted-foreground">
                          Voor het tonen van Google beoordelingen op onze website
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-medium">Calendly (indien gebruikt)</h4>
                        <p className="text-sm text-muted-foreground">
                          Voor het inplannen van afspraken via onze website
                        </p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-medium">WhatsApp Business</h4>
                        <p className="text-sm text-muted-foreground">
                          Voor de WhatsApp chat-functionaliteit
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cookies beheren */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Cookies beheren</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Browser instellingen</h4>
                    <p className="text-muted-foreground">
                      U kunt cookies beheren via de instellingen van uw browser. Houd er rekening mee dat het uitschakelen 
                      van cookies de werking van onze website kan beïnvloeden.
                    </p>
                  </div>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 dark:bg-amber-950/20 dark:border-amber-900/30">
                    <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">Let op</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      Het volledig uitschakelen van cookies kan ertoe leiden dat bepaalde functionaliteiten van onze website 
                      niet meer naar behoren werken, zoals het invullen van contactformulieren of het inplannen van afspraken.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Vragen over ons cookiebeleid?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Heeft u vragen over ons gebruik van cookies of dit cookiebeleid? 
                  Neem dan gerust contact met ons op.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div>
                    <p className="font-medium">E-mail</p>
                    <p className="text-muted-foreground">info@compleetverzekerd.nl</p>
                  </div>
                  <div>
                    <p className="font-medium">Telefoon</p>
                    <a href="tel:0782200021" className="text-muted-foreground hover:text-primary transition-colors">078 - 220 00 21</a>
                  </div>
                </div>
                
                <Separator className="my-4" />
                
                <p className="text-sm text-muted-foreground">
                  Dit cookiebeleid is voor het laatst bijgewerkt op: {new Date().toLocaleDateString('nl-NL', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Cookies;